import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { DashboardData, DashboardRange } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { BarList, Delta, StatTile, percentChange } from '../components/charts';
import { MonthlyStatusChart } from '../components/MonthlyStatusChart';
import { ErrorBanner } from '../components/ui';
import {
  IconAward,
  IconContact,
  IconCopy,
  IconPhoneOff,
  IconSpark,
  IconUserX,
  IconUsers,
} from '../components/icons';

const RANGES: Array<{ key: DashboardRange; label: string }> = [
  { key: 'all', label: 'All time' },
  { key: 'week', label: 'Last 7 days' },
  { key: 'month', label: 'This month' },
];

function useDashboard(range: DashboardRange, counsellorId?: string) {
  const params = new URLSearchParams({ range });
  if (counsellorId) params.set('counsellor_id', counsellorId);
  return useQuery({
    queryKey: ['dashboard', range, counsellorId ?? 'all'],
    queryFn: () => api<DashboardData>(`/dashboard?${params.toString()}`),
    staleTime: 60_000,
  });
}

function RangeSwitch({ value, onChange }: { value: DashboardRange; onChange: (r: DashboardRange) => void }) {
  return (
    <div className="seg" role="tablist" aria-label="Time range">
      {RANGES.map((r) => (
        <button
          key={r.key}
          type="button"
          role="tab"
          aria-selected={value === r.key}
          className={value === r.key ? 'active' : ''}
          onClick={() => onChange(r.key)}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

const rangeDateSpan = (range: DashboardData['range']) => {
  if (!range.start || !range.end) return null;
  return `${shortDate(range.start.slice(0, 10))} – ${shortDate(range.end.slice(0, 10))}`;
};

export function DashboardPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const counsellorId = searchParams.get('counsellor_id') || undefined;

  const [range, setRange] = useState<DashboardRange>('all');
  const { data, isPending, isFetching, error } = useDashboard(range, counsellorId);

  const setCounsellorId = (id?: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) {
      next.set('counsellor_id', id);
    } else {
      next.delete('counsellor_id');
    }
    setSearchParams(next);
  };

  if (error) {
    return (
      <div className="page">
        <h1>Dashboard</h1>
        <ErrorBanner error={error} />
      </div>
    );
  }

  if (isPending || !data) {
    return (
      <div className="page">
        <div className="skeleton" style={{ height: 28, width: 200, marginBottom: 20 }} />
        <div className="kpi-row">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="skeleton" style={{ height: 88, borderRadius: 10 }} />
          ))}
        </div>
        <div className="skeleton" style={{ height: 260, borderRadius: 10, marginTop: 18 }} />
      </div>
    );
  }

  const { users, leads, periods, monthly, selected_counsellor } = data;
  const currentYear = new Date().getFullYear();
  const tm = periods.this_month;
  const lm = periods.last_month;

  const dayOfMonth = new Date().getDate();
  const daysInLastMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 0).getDate();
  const partialMonth = dayOfMonth < daysInLastMonth;

  const statusBars = leads.by_status
    .filter((s) => s.count > 0)
    .map((s) => ({ label: s.name, value: s.count, dotColor: s.color }));

  const sourceBars = leads.by_source.map((s) => ({ label: s.name, value: s.count }));

  const dnpStatus = leads.by_status.find((s) => s.key === 'dnp');
  const interested = leads.by_status.find((s) => s.key === 'interested');
  const notInterested = leads.by_status.find((s) => s.key === 'not_interested');

  const totalHint =
    range === 'all' ? 'all time' : range === 'week' ? 'created in the last 7 days' : 'created this month so far';
  const dateSpan = rangeDateSpan(data.range);

  const isIndividualView = Boolean(selected_counsellor || user?.scope === 'own');
  const activeCounsellorName = selected_counsellor?.full_name ?? (user?.scope === 'own' ? user.full_name : null);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{activeCounsellorName ? `${activeCounsellorName}'s Dashboard` : 'Dashboard'}</h1>
          <p className="sub">
            {activeCounsellorName
              ? `Individual Team Specialist Dashboard · ${selected_counsellor?.team_name ? selected_counsellor.team_name + ' Team' : 'Individual workload'}`
              : user?.scope === 'all'
                ? 'Everything across the organisation'
                : "Your teams' numbers"}
            {' · '}
            {leads.added_today} added today
          </p>
        </div>
        <div className="page-head-actions" style={{ gap: 12 }}>
          {user?.scope !== 'own' && leads.by_counsellor.length > 0 && (
            <select
              className="form-control"
              style={{ width: 'auto', minWidth: 200 }}
              value={counsellorId || ''}
              onChange={(e) => setCounsellorId(e.target.value || undefined)}
              aria-label="Select team specialist"
            >
              <option value="">All Team Specialists (Overview)</option>
              {leads.by_counsellor.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.total} leads)
                </option>
              ))}
            </select>
          )}

          {isFetching && !isPending && <span className="spinner" title="Refreshing" />}
          <RangeSwitch value={range} onChange={setRange} />
        </div>
      </div>

      {/* Individual Counsellor Spotlight Banner */}
      {selected_counsellor && (
        <div
          className="card"
          style={{
            marginBottom: 20,
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          }}
        >
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'var(--accent)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 16,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {selected_counsellor.full_name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
                  {selected_counsellor.full_name}
                  <span className="tag tag-accent" style={{ marginLeft: 8 }}>
                    Team Specialist
                  </span>
                </h3>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
                  {selected_counsellor.email} {selected_counsellor.team_name ? `· Team: ${selected_counsellor.team_name}` : ''}
                </div>
              </div>
            </div>

            <button className="btn btn-ghost btn-sm" onClick={() => setCounsellorId(undefined)}>
              ← Back to All Team Specialists
            </button>
          </div>
        </div>
      )}

      {/* Action items banner for counsellor follow-ups */}
      {isIndividualView && (Boolean(leads.followups_today) || Boolean(leads.followups_overdue)) && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {Boolean(leads.followups_today) && (
            <div className="banner banner-info" style={{ flex: 1, minWidth: 260 }}>
              <div>
                <strong>{leads.followups_today} follow-up{leads.followups_today! > 1 ? 's' : ''} scheduled for today.</strong>{' '}
                <Link to="/leads?followup=today" style={{ fontWeight: 600, textDecoration: 'underline' }}>
                  Work them now →
                </Link>
              </div>
            </div>
          )}
          {Boolean(leads.followups_overdue) && (
            <div className="banner banner-warning" style={{ flex: 1, minWidth: 260 }}>
              <div>
                <strong>{leads.followups_overdue} overdue follow-up{leads.followups_overdue! > 1 ? 's' : ''} needing attention!</strong>{' '}
                <Link to="/leads?followup=overdue" style={{ fontWeight: 600, textDecoration: 'underline' }}>
                  View overdue leads →
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Headline numbers */}
      <div className="section-eyebrow">
        {isIndividualView ? `${activeCounsellorName}'s Leads` : 'Leads'} · {data.range.label}
        {dateSpan && <span>{dateSpan}</span>}
      </div>
      <div className="kpi-row">
        <StatTile label="Total leads" value={leads.total} hint={totalHint} icon={<IconContact />} />
        {!isIndividualView && (
          <StatTile
            label="Unassigned"
            value={leads.unassigned}
            tone={leads.unassigned > 0 ? 'warning' : 'default'}
            hint={leads.unassigned > 0 ? 'nobody working these' : 'all assigned'}
            icon={<IconUserX />}
          />
        )}
        <StatTile label="Interested" value={interested?.count ?? 0} hint="current status" icon={<IconSpark />} />
        <StatTile label="DNP" value={dnpStatus?.count ?? 0} hint="did not pick" icon={<IconPhoneOff />} />
        <StatTile label="Not interested" value={notInterested?.count ?? 0} hint="current status" icon={<IconUserX />} />
        <StatTile
          label="Converted"
          value={leads.by_category.won ?? 0}
          hint={`${leads.total > 0 ? (((leads.by_category.won ?? 0) / leads.total) * 100).toFixed(1) : '0'}% of leads in range`}
          icon={<IconAward />}
        />
      </div>

      {!isIndividualView && (
        <>
          <div className="section-eyebrow" style={{ marginTop: 20 }}>
            Team · right now
            <span>not affected by the range filter above</span>
          </div>
          <div className="kpi-row">
            <StatTile label="Users" value={users.total} hint={`${users.by_role.length} roles`} icon={<IconUsers />} />
            <StatTile label="Active users" value={users.active} icon={<IconUsers />} />
            <StatTile
              label="Deactivated"
              value={users.inactive}
              tone={users.inactive > 0 ? 'warning' : 'default'}
              hint={users.inactive > 0 ? 'cannot sign in' : 'none'}
              icon={<IconUserX />}
            />
            <StatTile label="Duplicates flagged" value={leads.duplicates} hint="in range, hidden from the list" icon={<IconCopy />} />
          </div>
        </>
      )}

      {/* Monthly Chart */}
      <div className="card" style={{ marginTop: 22 }}>
        <div className="card-head">
          <h2>{isIndividualView ? `${activeCounsellorName}'s Leads by Month` : 'Leads received by month'}</h2>
          <div className="card-actions">
            <span className="chip chip-plain">{currentYear}</span>
          </div>
        </div>
        <div className="card-body">
          {monthly.length > 0 ? (
            <MonthlyStatusChart data={monthly} height={300} />
          ) : (
            <p style={{ color: 'var(--muted)', margin: 0 }}>No leads yet this year.</p>
          )}
          <p className="hint" style={{ marginTop: 4 }}>
            Click a status in the legend to isolate it — click again to show every status.
          </p>
        </div>
      </div>

      {/* Month comparison */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-head">
          <h2>This month vs last month</h2>
          <div className="card-actions">
            <span className="chip chip-plain">
              {tm.start.slice(0, 7)} vs {lm.start.slice(0, 7)}
            </span>
          </div>
        </div>
        <div className="card-body">
          <p className="hint" style={{ marginTop: -4, marginBottom: 14 }}>
            A fixed calendar-month comparison — always this month vs last month, regardless of the
            range filter at the top of the page.
          </p>

          {partialMonth && (
            <div className="banner banner-info" style={{ marginBottom: 14 }}>
              <div>
                This month is only {dayOfMonth} days in, so it is compared against a full{' '}
                {daysInLastMonth}-day month. Expect the percentages to look low until month end.
              </div>
            </div>
          )}

          <div className="tw">
            <table className="data">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th className="num">This month</th>
                  <th className="num">Last month</th>
                  <th className="num">Change</th>
                </tr>
              </thead>
              <tbody>
                <ComparisonRow label="Leads received" now={tm.received} then={lm.received} goodWhenUp />
                <ComparisonRow label="Converted (admitted)" now={tm.converted} then={lm.converted} goodWhenUp />
                <ComparisonRow label="Interested" now={tm.interested} then={lm.interested} goodWhenUp />
                <ComparisonRow label="DNP (did not pick)" now={tm.dnp} then={lm.dnp} goodWhenUp={false} />
                <ComparisonRow label="Lost" now={tm.lost} then={lm.lost} goodWhenUp={false} />
                <tr>
                  <td><strong>Conversion rate</strong></td>
                  <td className="num"><strong>{tm.conversion_rate}%</strong></td>
                  <td className="num">{lm.conversion_rate}%</td>
                  <td className="num">
                    <Delta pct={tm.conversion_rate - lm.conversion_rate} goodWhenUp />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="hint" style={{ marginTop: 10 }}>
            “Converted” counts leads that moved into an Admitted status during the month — not leads
            created that month that happen to be admitted now.
          </p>
        </div>
      </div>

      <div className="dash-split" style={{ marginTop: 18 }}>
        <div className="card">
          <div className="card-head">
            <h2>Leads by status</h2>
            <div className="card-actions">
              <span className="chip chip-plain">{data.range.label}</span>
            </div>
          </div>
          <div className="card-body">
            {statusBars.length > 0 ? (
              <BarList data={statusBars} total={leads.total} />
            ) : (
              <p style={{ color: 'var(--muted)', margin: 0 }}>No leads in this range.</p>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Leads by source</h2>
            <div className="card-actions">
              <span className="chip chip-plain">{data.range.label}</span>
            </div>
          </div>
          <div className="card-body">
            {sourceBars.length > 0 ? (
              <BarList data={sourceBars} total={leads.total} />
            ) : (
              <p style={{ color: 'var(--muted)', margin: 0 }}>No leads in this range.</p>
            )}
          </div>
        </div>
      </div>

      {/* Counsellor workload table */}
      {!isIndividualView && (
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-head">
            <h2>Team Specialist workload</h2>
            <div className="card-actions">
              <span className="chip chip-plain">{data.range.label}</span>
              <Link className="btn btn-sm" to="/leads?unassigned=true">View unassigned</Link>
            </div>
          </div>
          <div className="card-body" style={{ paddingTop: 6 }}>
            <div className="tw">
              <table className="data">
                <thead>
                  <tr>
                    <th>Team Specialist</th>
                    <th className="num">Total</th>
                    <th className="num">Open</th>
                    <th className="num">Converted</th>
                    <th className="num">Rate</th>
                    <th style={{ textAlign: 'right' }}>Individual Dashboard</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.by_counsellor.map((c) => (
                    <tr key={c.id}>
                      <td><strong>{c.name}</strong></td>
                      <td className="num">{c.total}</td>
                      <td className="num">{c.open}</td>
                      <td className="num">{c.won}</td>
                      <td className="num">{c.total > 0 ? ((c.won / c.total) * 100).toFixed(1) : '0.0'}%</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => setCounsellorId(c.id)}
                          title={`View ${c.name}'s individual dashboard`}
                        >
                          View Dashboard →
                        </button>
                      </td>
                    </tr>
                  ))}
                  {leads.by_counsellor.length === 0 && (
                    <tr><td colSpan={6} style={{ color: 'var(--muted)' }}>No leads assigned in this range.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ComparisonRow({
  label,
  now,
  then,
  goodWhenUp,
}: {
  label: string;
  now: number;
  then: number;
  goodWhenUp: boolean;
}) {
  return (
    <tr>
      <td>{label}</td>
      <td className="num"><strong>{now.toLocaleString()}</strong></td>
      <td className="num" style={{ color: 'var(--muted)' }}>{then.toLocaleString()}</td>
      <td className="num"><Delta pct={percentChange(now, then)} goodWhenUp={goodWhenUp} /></td>
    </tr>
  );
}
